import { concat, fn, hash } from '@ember/helper';
import { action } from '@ember/object';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import didUpdate from '@ember/render-modifiers/modifiers/did-update';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import willDestroy from '@ember/render-modifiers/modifiers/will-destroy';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import optional from '@nullvoxpopuli/ember-composable-helpers/helpers/optional';
import queue from '@nullvoxpopuli/ember-composable-helpers/helpers/queue';
import { focusTrap } from 'ember-focus-trap';
import momentFormat from 'ember-moment/helpers/moment-format';
import or from 'ember-truth-helpers/helpers/or';
import moment, { utc } from 'moment-timezone';
import BvBtn from 'volta/components/bv-btn';
import BvCalendar from 'volta/components/bv-calendar';
import BvIcon from 'volta/components/bv-icon';
import BvInlineEdit from 'volta/components/bv-inline-edit';
import BvPopover from 'volta/components/bv-popover';
import BvTextField from 'volta/components/bv-text-field';
import orElse from 'volta/helpers/or-else';
import { validDateOrUndefined } from 'volta/utils/date-utils';

import type { Dropdown } from 'ember-basic-dropdown/components/basic-dropdown';
import type { PowerCalendarAPI } from 'ember-power-calendar/components/power-calendar';
import type { PowerCalendarDay } from 'ember-power-calendar/utils';
import type { BvPopoverSignature } from 'volta/components/bv-popover';
import type { TDate } from 'volta/models/types/calendar';
import type { BvFocusUnitModifier } from 'volta/components/bv-focus-zone';
import type { BvInlineEditApi } from 'volta/components/bv-inline-edit';
import type { BvCalendarSignature } from './../bv-calendar';
import type { BvTextFieldSignature } from './../bv-text-field';

type PopoverArgs = Pick<
  BvPopoverSignature['Args'],
  | 'horizontalPosition'
  | 'matchTriggerWidth'
  | 'onClose'
  | 'onOpen'
  | 'preventScroll'
  | 'renderInPlace'
>;

export type BvDateFieldArgs = Omit<BvTextFieldSignature['Args'], 'value' | 'onChange'> &
  PopoverArgs & {
    value?: TDate;
    minDate?: TDate;
    maxDate?: TDate;
    closeOnSelect?: boolean;
    withoutCalendar?: boolean;
    dateFormat?: string; // !BETTERTYPE!
    timeZone?: string; // !BETTERTYPE!
    testName?: string;
    dataName?: string;
    fullWidth?: boolean;
    allowClear?: boolean;
    focusZoneUnit?: BvFocusUnitModifier;
    size?: BvCalendarSignature['Args']['size'];
    iconClass?: string;
    enableFocusTrap?: boolean;
    onChange?: (value?: TDate) => void;
    onBlur?: (e: FocusEvent) => void;
    onFocus?: (e: FocusEvent) => void;
    onEnter?: (value?: TDate) => void;
    onClearButtonClick?: () => void;
  };

export interface BvDateFieldSignature {
  Args: BvDateFieldArgs;
  Blocks: {
    days: [
      {
        popover: BvInlineEditApi['popover'];
        calendar: PowerCalendarAPI;
        minDate?: TDate;
        maxDate?: TDate;
      }
    ];
    menu: [{ popover: BvInlineEditApi['popover']; calendar: PowerCalendarAPI }];
    trigger: [{ popover: BvInlineEditApi['popover'] }];
  };
  Element: HTMLElement;
}

export default class BvDateFieldComponent extends Component<BvDateFieldSignature> {
  @tracked calCenter = this.args.value
    ? moment.utc(this.args.value).toDate()
    : moment.utc().toDate();
  @tracked inputError: boolean = false;

  get validatedDate() {
    return this.validateInputDate(this.args.value)?.toDate();
  }

  get disabled() {
    return this.args.disabled === true || this.args.readOnly === true;
  }

  get allowClear() {
    return (this.args.clearButton || this.args.allowClear) == true;
  }

  @action
  onChange(
    day: PowerCalendarDay,
    _calendar: PowerCalendarAPI,
    e: InputEvent,
    afterSelect?: (e: InputEvent) => void
  ) {
    e.stopPropagation();
    const { closeOnSelect = true } = this.args;
    this.inputError = false;

    this.args.onChange?.(day.date);

    if (closeOnSelect && typeof afterSelect === 'function') {
      afterSelect(e);
    }
  }

  @action
  onInputChange(value: string | number) {
    const mDate = this.validateInputDate(value);
    if (mDate) {
      this.inputError = false;
      this.calCenter = mDate.toDate();

      if (this.args.withoutCalendar) {
        this.args.onChange?.(mDate.toDate());
      }
    } else {
      this.inputError = true;
    }
  }

  @action
  handleInlineEditSave(event: Event) {
    if (!event?.target) {
      return;
    }
    const { value } = event.target as HTMLInputElement;
    this.handleTextFieldEnter(value);
  }

  @action
  handleTextFieldEnter(value: moment.MomentInput) {
    const { onEnter, onChange } = this.args;
    const validDate = this.validateInputDate(value);

    if (validDate) {
      this.inputError = false;
      if (typeof onEnter === 'function') {
        onEnter?.(validDate.toDate());
      } else {
        onChange?.(validDate.toDate());
      }
    }
  }

  @action
  handleCenterChange(center: PowerCalendarDay) {
    if (center.date) {
      this.calCenter = moment.utc(center.date).toDate();
    }
  }

  @action
  handleParentValueChange() {
    this.calCenter = moment.utc(this.args.value).toDate();
  }

  @action
  handlePopoverOpen(dropdown: Dropdown, e?: Event) {
    this.calCenter = moment.utc(this.args.value).toDate();
    return this.args.onOpen?.(dropdown, e);
  }

  @action
  handleClear(_id: string, _e: InputEvent) {
    this.calCenter = moment.utc().toDate();
    this.inputError = false;
    this.args.onChange?.(null);
  }

  /**
   * This function validate the date value input.
   * It checks the min and max date constraints as weel as general parsing validity.
   *
   * @param date Input date
   * @returns The parse Date or undefined if invalid or undefined
   */
  validateInputDate(date: moment.MomentInput) {
    const { minDate, maxDate } = this.args;
    const vMinDate = validDateOrUndefined(minDate);
    const vMaxDate = validDateOrUndefined(maxDate);
    const vDate = validDateOrUndefined(date);

    return vDate &&
      ((vMinDate && moment.utc(vDate).isBefore(vMinDate)) ||
        (vMaxDate && moment.utc(vDate).isAfter(vMaxDate)))
      ? undefined
      : vDate;
  }

  <template>
    {{#let
      (component
        BvTextField
        value=(if @value (momentFormat (utc @value) (or @dateFormat 'L') timeZone='UTC'))
        type='text'
        disabled=@disabled
        label=@label
        labelInfo=@labelInfo
        focused=@focused
        placeholder=@placeholder
        name=@name
        onFocus=@onFocus
        onBlur=@onBlur
        prefix=@prefix
        suffix=(orElse
          @suffix
          (unless @withoutCalendar (component BvIcon glyph='calendar-outline' class=@iconClass))
        )
        error=(orElse @error this.inputError)
        readOnly=@readOnly
        onChange=(unless @readOnly this.onInputChange)
        inline=@inline
        labelWrapperClass=@labelWrapperClass
        clearButton=this.allowClear
        onClearButtonClick=(if this.allowClear (orElse @onClearButtonClick this.handleClear))
        onEnter=this.handleTextFieldEnter
      )
      (component
        BvCalendar
        size=(orElse @size 'sm')
        fullWidth=@fullWidth
        center=this.calCenter
        onCenterChange=this.handleCenterChange
        selected=this.validatedDate
        onSelect=this.onChange
        disabled=this.disabled
        minDate=@minDate
        maxDate=@maxDate
      )
      (orElse @preventScroll true)
      as |TextField Calendar preventScroll|
    }}
      {{#if (has-block 'trigger')}}
        <BvInlineEdit
          @isMenu={{true}}
          @preventScroll={{preventScroll}}
          @hideDefaultActions={{true}}
          @horizontalPosition={{@horizontalPosition}}
          @disabled={{this.disabled}}
          @onSave={{this.handleInlineEditSave}}
          @focusZoneUnit={{@focusZoneUnit}}
          @testName={{@testName}}
          @matchTriggerWidth={{@matchTriggerWidth}}
          @renderInPlace={{@renderInPlace}}
          @onOpen={{this.handlePopoverOpen}}
          @enableFocusTrap={{@enableFocusTrap}}
          ...attributes
        >
          <:default as |d|>
            {{yield (hash popover=d.popover) to='trigger'}}
          </:default>
          <:editing as |e|>
            {{#unless @withoutCalendar}}
              <Calendar
                @afterSelect={{e.close}}
                class='bv-date-field bv-date-field--inline'
                as |calendar|
              >
                <div class='bv-date-field__input'>
                  <TextField
                    @focused={{true}}
                    @onEnter={{fn (queue e.close this.handleTextFieldEnter)}}
                    class={{if @fullWidth 'u-full-width'}}
                  />
                </div>
                <div class='bv-date-field__calendar'>
                  {{! @glint-ignore -- calendar.Nav is a component}}
                  <calendar.Nav />
                  {{#if (has-block 'days')}}
                    {{yield
                      (hash popover=e.popover calendar=calendar minDate=@minDate maxDate=@maxDate)
                      to='days'
                    }}
                  {{else}}
                    {{! @glint-ignore -- calendar.Nav is a component}}
                    <calendar.Days @minDate={{@minDate}} @maxDate={{@maxDate}} />
                  {{/if}}
                </div>
                {{#if (has-block 'menu')}}
                  <div class='bv-date-field__menu'>
                    {{yield (hash popover=e.popover calendar=calendar) to='menu'}}
                  </div>
                {{/if}}
              </Calendar>
            {{/unless}}
          </:editing>
        </BvInlineEdit>
      {{else if @withoutCalendar}}
        <TextField
          @onEnter={{this.handleTextFieldEnter}}
          @disabled={{@disabled}}
          @name={{@testName}}
          class={{if @fullWidth 'u-full-width'}}
          ...attributes
        />
      {{else}}
        <BvPopover
          @horizontalPosition={{@horizontalPosition}}
          @preventScroll={{preventScroll}}
          @onClose={{@onClose}}
          @disabled={{this.disabled}}
          @onOpen={{this.handlePopoverOpen}}
          @matchTriggerWidth={{orElse @matchTriggerWidth true}}
          @renderInPlace={{@renderInPlace}}
          as |popover|
        >
          <TextField
            @onEnter={{fn (queue popover.close this.handleTextFieldEnter)}}
            @disabled={{@disabled}}
            @name={{@testName}}
            @suffix={{component
              BvBtn
              iconLeft='calendar'
              subtle=true
              size='sm'
              onClick=popover.toggle
              class=@iconClass
              testName=(concat @testName '-calendar-btn')
            }}
            class={{if @fullWidth 'u-full-width'}}
            ...attributes
            {{willDestroy
              (if popover.isOpen (fn this.handleTextFieldEnter this.validatedDate) (optional))
            }}
            {{popover.trigger}}
          />

          <popover.Content
            {{focusTrap
              isActive=popover.isOpen
              shouldSelfFocus=true
              focusTrapOptions=popover.focusTrapOptions
            }}
          >
            <popover.Body @flush={{true}}>
              <Calendar
                @fullWidth={{true}}
                @afterSelect={{popover.close}}
                {{didUpdate this.handleParentValueChange @value}}
                as |calendar|
              >
                <div class='bv-date-field__calendar'>
                  {{! @glint-ignore -- calendar.Nav is a component}}
                  <calendar.Nav />
                  {{#if (has-block 'days')}}
                    {{yield
                      (hash popover=popover calendar=calendar minDate=@minDate maxDate=@maxDate)
                      to='days'
                    }}
                  {{else}}
                    {{! @glint-ignore -- calendar.Days is a component}}
                    <calendar.Days @minDate={{@minDate}} @maxDate={{@maxDate}} />
                  {{/if}}
                </div>
                {{#if (has-block 'menu')}}
                  <div class='bv-date-field__menu'>
                    {{yield (hash popover=popover calendar=calendar) to='menu'}}
                  </div>
                {{/if}}
              </Calendar>
            </popover.Body>
          </popover.Content>
        </BvPopover>
      {{/if}}
    {{/let}}
  </template>
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    BvDateField: typeof BvDateFieldComponent;
    'bv-date-field': typeof BvDateFieldComponent;
  }
}
